// libs/extensions/src/lib/plugins/jmkw-plugins.service.ts

import { inject, Injectable } from '@angular/core';
import { Observable, combineLatest, firstValueFrom, of } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { Plugin, PluginMap } from '@jeppesen-foreflight/jmkw-types';
import { LOCATION_PLUGIN } from './location/location.plugin.service';
import { pluginArrayToMap } from './plugin-utils';
import { TerrainPluginService } from './jeppesen/terrain/terrain.plugin.service';
import { TwcPluginService } from './the-weather-company/plugins/twc.plugin.service';
import { VIEW_TOGGLE_PLUGIN } from './view-toggle/view-toggle.plugin';
import { THEME_PLUGIN } from './theme/theme.plugin';
import { AviationPluginService } from './jeppesen/aviation/aviation.plugin.service';

@Injectable({ providedIn: 'root' })
export class JmkwPluginService {
  private readonly _weatherPlugin: Observable<Plugin>;
  private readonly _aviationPlugin: Observable<Plugin>;
  private readonly _terrainPlugin: Observable<Plugin>;

  private readonly _plugins$: Observable<Plugin>[];

  private readonly _staticPlugins: Plugin[] = [
    VIEW_TOGGLE_PLUGIN,
    THEME_PLUGIN,
    LOCATION_PLUGIN
    // Uncomment these if they are needed in the future
    // this._culturalPlugin,
    // this._navPlugin,
    // this._allTerraPlugin,
    // IFR_TOGGLE_PLUGIN
  ];

  constructor() {
    const weatherPluginService = inject(TwcPluginService);
    const aviationPluginService = inject(AviationPluginService);
    const terrainPluginService = inject(TerrainPluginService);

    this._weatherPlugin = weatherPluginService.create({
      apiKey: '4ad9c80ae23f4a0299c80ae23f1a021d'
    });
    // Aviation plugin already creates obstacles, cultural, and navigation as children
    this._aviationPlugin = aviationPluginService.create();
    this._terrainPlugin = terrainPluginService.create();

    this._plugins$ = [
      this._aviationPlugin,
      this._weatherPlugin,
      this._terrainPlugin
    ];
  }

  pluginMap$(): Observable<PluginMap> {
    return combineLatest([
      ...this._staticPlugins.map((plugin) => of(plugin)),
      ...this._plugins$
    ]).pipe(map((plugins) => pluginArrayToMap(plugins)));
  }

  getJmkwPlugins$(): Observable<Plugin[]> {
    return combineLatest(this._plugins$).pipe(
      map((plugins) => [...this._staticPlugins, ...plugins]),
      take(1)
    );
  }

  async getJmkwPlugins(): Promise<Plugin[]> {
    return firstValueFrom(this.getJmkwPlugins$());
  }

  async getJmkwPluginMap(): Promise<PluginMap> {
    return firstValueFrom(this.pluginMap$());
  }
}
